import { useTranslation } from 'react-i18next';
import { IconButton } from '@hotjar/hotsauce';

import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { useCurrentSiteId } from '@hotjar/site';
import { event } from '@hotjar/webapp/tracking/tools/hotjar';

import { triggerDownload } from '../trigger-download';
import { useRefreshedRecordingHighlight } from '../use-refreshed-recording-highlight';

const RECORDING_HIGHLIGHT_DOWNLOADED = 'heatmap_highlight_downloaded';

interface RecordingDownloadButtonProps {
  highlight: {
    id: string;
    contentStatus: 'creating' | unknown;
    metadata: {
      videoUrl: string | null;
    };
  };
  uiPosition?: UIPosition;
}

export function RecordingDownloadButton({
  highlight: initialHighlight,
  uiPosition,
}: RecordingDownloadButtonProps): JSX.Element {
  const { t } = useTranslation();
  const currentSiteId = useCurrentSiteId();

  const { data: highlight } = useRefreshedRecordingHighlight(currentSiteId, initialHighlight);

  const handleDownload = () => {
    if (highlight.metadata.videoUrl === null) {
      return;
    }

    triggerDownload(highlight.metadata.videoUrl, `clip_${highlight.id}.mp4`);

    if (currentSiteId) {
      sendHighlightEvent(
        currentSiteId,
        { highlightId: highlight.id, eventType: HighlightsEventType.HIGHLIGHT_DOWNLOADED },
        { uiPosition },
      );
    }

    event(RECORDING_HIGHLIGHT_DOWNLOADED);
  };

  return (
    <IconButton
      icon="download"
      isDisabled={highlight.contentStatus !== 'done'}
      onClick={handleDownload}
      kind="ghost"
      size="s"
      tooltipText={t('highlights:single.download')}
    />
  );
}
